import { computed, ref } from 'vue'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { ErrorCodes, type BaseError, MetaErrorCodes, type Meta } from '@cplug/frontend-core/models'
import { BaseCRUDStore } from '@cplug/frontend-core/stores'
import type { LoggedUser } from '../models/LoggedUser'
import { CPLoggedUserGateway } from '../gateways/CPLoggedUserGateway'
import type { UserPermission } from '~/domain/dashboard/user/permissions/models/UserPermision'
import { useIsValidEmail } from '~/shared/composables/useEmail'

class LoggedUserStore extends BaseCRUDStore<LoggedUser> {
  protected gateway = CPLoggedUserGateway.instance()

  protected findErrors (model: LoggedUser): BaseError[] {
    const defaultErrors = (meta: Meta): BaseError => ({ code: ErrorCodes.DataValidation, meta })
    const errors: BaseError[] = []

    if ((model.name?.trim().length ?? 0) <= 0) {
      errors.push(defaultErrors({ name: MetaErrorCodes.Empty }))
    }

    if ((model.name?.trim().length ?? 0) > 120) {
      errors.push(defaultErrors({ name: MetaErrorCodes.LimitExceeded }))
    }

    if ((model.email?.trim().length ?? 0) <= 0) {
      errors.push(defaultErrors({ email: MetaErrorCodes.Empty }))
    }

    if ((model.email?.trim().length ?? 0) > 0 && !useIsValidEmail(model.email)) {
      errors.push(defaultErrors({ email: MetaErrorCodes.WrongFormat }))
    }

    if ((model.email?.trim().length ?? 0) > 120) {
      errors.push(defaultErrors({ email: MetaErrorCodes.LimitExceeded }))
    }

    return errors
  }
}

const useLoggedUserStore = defineStore('user/logged-user', () => {
  const loggedUserStore = new LoggedUserStore()
  const crudStore = loggedUserStore.defaultStore()
  const permissionsData = ref<UserPermission[] | undefined>()
  const permissionErrors = ref<BaseError[]>([])
  const permissions = computed(() => permissionsData.value ?? [])
  const permissionSet = computed(() => new Set(permissions.value.map(permission => permission.name)))

  const get = async (): Promise<void> => {
    (await CPLoggedUserGateway.instance().getLoggedUser())
      .ifSuccess((m) => {
        crudStore.model.value = m
      })
      .ifError(error => crudStore.errors.value.push(error))
  }

  async function getPermissions (): Promise<void> {
    permissionErrors.value = [] as BaseError[]
    (await CPLoggedUserGateway.instance().getPermissions())
      .ifSuccess((list) => {
        permissionsData.value = list
      })
      .ifError(error => permissionErrors.value.push(error))
  }

  function clearPermissions (): void {
    permissionsData.value = undefined
    permissionErrors.value = [] as BaseError[]
  }

  return {
    ...crudStore,
    get,
    permissionsData,
    permissions,
    permissionSet,
    permissionErrors,
    getPermissions,
    clearPermissions
  }
})

export default useLoggedUserStore

/* c8 ignore next 3 */
if ((import.meta as any).hot) {
  (import.meta as any).hot.accept(acceptHMRUpdate(useLoggedUserStore, (import.meta as any).hot))
}
